import { useDroppable } from "@dnd-kit/core"
import { useDraggable } from "@dnd-kit/core"
import { formatCurrency } from "@/lib/utils"
import { Building2, Clock, User } from "lucide-react"
import type { DealBoardColumn, DealCard } from "@/lib/types"

// ── Kanban Card ──────────────────────────────────────────────────

export function KanbanCardComponent({
  card,
  onClick,
  isDragOverlay,
}: {
  card: DealCard
  onClick?: () => void
  isDragOverlay?: boolean
}) {
  const daysColor =
    card.days_in_phase > 14
      ? "#EF4444"
      : card.days_in_phase > 7
        ? "#F59E0B"
        : undefined

  return (
    <div
      onClick={onClick}
      className={`rounded-lg border p-3 transition-shadow cursor-pointer bg-card ${
        isDragOverlay ? "border-primary shadow-lg opacity-95" : "shadow-sm hover:shadow-md"
      }`}
      style={isDragOverlay ? { width: 260 } : undefined}
    >
      {/* Name */}
      <p className="text-sm font-medium leading-snug mb-1.5 line-clamp-2 text-foreground">
        {card.name}
      </p>

      {/* Company */}
      {card.company && (
        <div className="flex items-center gap-1.5 mb-2">
          <Building2 size={12} className="text-muted-foreground" />
          <span className="text-xs truncate text-muted-foreground">
            {card.company.name}
          </span>
        </div>
      )}

      {/* Value */}
      {card.value != null && Number(card.value) > 0 && (
        <p className="text-sm font-semibold font-mono tabular-nums mb-2 text-foreground">
          {formatCurrency(Number(card.value), card.currency)}
        </p>
      )}

      {/* Footer: owner + days */}
      <div className="flex items-center justify-between">
        {card.owner ? (
          <div className="flex items-center gap-1.5">
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
              {card.owner.first_name[0]}
              {card.owner.last_name[0]}
            </div>
            <span className="text-xs text-muted-foreground">
              {card.owner.first_name} {card.owner.last_name[0]}.
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-muted-foreground">
            <User size={12} />
            <span className="text-xs">—</span>
          </div>
        )}
        <div className="flex items-center gap-1" style={daysColor ? { color: daysColor } : undefined}>
          <Clock size={11} className={daysColor ? "" : "text-muted-foreground"} />
          <span className={`text-xs font-medium ${daysColor ? "" : "text-muted-foreground"}`}>
            {card.days_in_phase}d
          </span>
        </div>
      </div>
    </div>
  )
}

// ── Draggable Card Wrapper ───────────────────────────────────────

function DraggableCard({
  card,
  onCardClick,
}: {
  card: DealCard
  onCardClick: (id: string) => void
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: card.id,
  })

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        opacity: isDragging ? 0.3 : 1,
        transition: isDragging ? undefined : "opacity 150ms ease",
      }}
      {...listeners}
      {...attributes}
    >
      <KanbanCardComponent card={card} onClick={() => onCardClick(card.id)} />
    </div>
  )
}

// ── Kanban Column ────────────────────────────────────────────────

export function KanbanColumn({
  column,
  onCardClick,
}: {
  column: DealBoardColumn
  onCardClick: (id: string) => void
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: column.phase.id,
  })

  return (
    <div
      className="flex flex-col min-w-[260px] max-w-[320px] flex-1 min-h-[200px]"
    >
      {/* Column Header */}
      <div className="flex items-center gap-2 rounded-t-lg px-3 py-2.5 border border-b-0 border-border bg-muted">
        <div
          className="h-2.5 w-2.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: column.phase.color }}
        />
        <span className="text-sm font-semibold truncate text-foreground">
          {column.phase.name}
        </span>
        <span
          className={`flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${
            column.count > 0 ? "" : "bg-muted text-muted-foreground"
          }`}
          style={column.count > 0 ? {
            backgroundColor: column.phase.color + "18",
            color: column.phase.color,
          } : undefined}
        >
          {column.count}
        </span>
        <div className="flex-1" />
        {column.count > 0 && (
          <span className="text-xs font-mono font-medium tabular-nums text-muted-foreground">
            {formatCurrency(Number(column.total_value))}
          </span>
        )}
      </div>

      {/* Cards area */}
      <div
        ref={setNodeRef}
        className="flex-1 space-y-2 rounded-b-lg border border-border p-2 overflow-y-auto transition-colors"
        style={{
          borderColor: isOver ? column.phase.color : undefined,
          backgroundColor: isOver ? column.phase.color + "08" : "transparent",
          maxHeight: "calc(100vh - 260px)",
        }}
      >
        {column.deals.map(card => (
          <DraggableCard key={card.id} card={card} onCardClick={onCardClick} />
        ))}

        {column.deals.length === 0 && (
          <div className="flex items-center justify-center rounded-md border border-dashed border-border py-8 text-xs text-muted-foreground">
            Brak szans
          </div>
        )}
      </div>
    </div>
  )
}
